import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ChevronDown, 
  ChevronRight,
  HelpCircle,
  ShoppingCart,
  DollarSign,
  PiggyBank,
  ListTodo,
  BarChart3,
  Settings,
  Sparkles,
  CreditCard,
  ArrowUpDown,
  Target,
  Receipt,
  Mail,
  Wallet,
  TrendingUp,
  Clock,
  Bell,
  Users,
  RefreshCw,
  Globe,
  Copy,
  Check,
  Mic,
  Archive,
  Share2,
  Download,
  Smartphone,
  Monitor
} from 'lucide-react';

const HelpPage = () => {
  const navigate = useNavigate();
  const supportEmail = 'support@getfinlist.com';

  // Expandable section states (only one open at a time)
  const initialSections = {
    gettingStarted: true,
    installation: false,
    transactions: false,
    budgetEnvelopes: false,
    shoppingLists: false,
    tasks: false,
    insights: false,
    savingsGoals: false,
    aiFeatures: false,
    settings: false,
    faq: false,
  };
  const [expandedSections, setExpandedSections] = useState(initialSections);

  const toggleSection = (section) => {
    setExpandedSections((prev) => {
      const isOpen = !!prev[section];
      const reset = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      return { ...reset, [section]: !isOpen };
    });
  };

  const colorThemes = {
    emerald: {
      bg: 'bg-emerald-100',
      bgDark: 'dark:bg-emerald-900/30',
      text: 'text-emerald-600',
      textDark: 'dark:text-emerald-400',
    },
    blue: {
      bg: 'bg-blue-100',
      bgDark: 'dark:bg-blue-900/30',
      text: 'text-blue-600',
      textDark: 'dark:text-blue-400',
    },
    purple: {
      bg: 'bg-purple-100',
      bgDark: 'dark:bg-purple-900/30',
      text: 'text-purple-600',
      textDark: 'dark:text-purple-400',
    },
    amber: {
      bg: 'bg-amber-100',
      bgDark: 'dark:bg-amber-900/30',
      text: 'text-amber-600',
      textDark: 'dark:text-amber-400',
    },
    teal: {
      bg: 'bg-teal-100',
      bgDark: 'dark:bg-teal-900/30',
      text: 'text-teal-600',
      textDark: 'dark:text-teal-400',
    },
  };

  const getColorTheme = (color) => colorThemes[color] || colorThemes.emerald;

  const Section = ({ id, title, icon: Icon, children, color = "emerald" }) => (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden mb-4">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSection(id);
        }}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
      >
        {(() => {
          const theme = getColorTheme(color);
          return (
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${theme.bg} ${theme.bgDark}`}>
            <Icon className={`w-5 h-5 ${theme.text} ${theme.textDark}`} />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{title}</h2>
        </div>
          );
        })()}
        {expandedSections[id] ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {expandedSections[id] && (
        <div className="p-4 pt-0 border-t border-gray-100 dark:border-slate-700">
          {children}
        </div>
      )}
    </div>
  );

  const Step = ({ number, title, children }) => (
    <div className="flex gap-3 mb-4">
      <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
        {number}
      </div>
      <div>
        <h4 className="font-medium text-gray-800 dark:text-white mb-1">{title}</h4>
        <p className="text-gray-600 dark:text-gray-300 text-sm">{children}</p>
      </div>
    </div>
  );

  const Tip = ({ children }) => (
    <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 p-3 rounded-r-lg mb-3">
      <p className="text-blue-800 dark:text-blue-300 text-sm">💡 <strong>Tip:</strong> {children}</p>
    </div>
  );

  const Important = ({ children }) => (
    <div className="bg-amber-50 dark:bg-amber-900/30 border-l-4 border-amber-400 p-3 rounded-r-lg mb-3">
      <p className="text-amber-800 dark:text-amber-300 text-sm">⚠️ <strong>Important:</strong> {children}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <HelpCircle className="w-10 h-10" />
            <div>
              <h1 className="text-2xl font-bold">FinList Help Center</h1>
              <p className="text-white/80">Your complete guide to mastering FinList</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        
        {/* Quick Overview */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-slate-800 dark:to-slate-800 rounded-xl p-6 mb-6 border border-emerald-100 dark:border-slate-700">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-3">👋 Welcome to FinList!</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            FinList is your all-in-one personal finance manager that combines <strong>smart shopping lists</strong>, 
            <strong> envelope budgeting</strong>, <strong>savings goals</strong>, <strong>task management</strong>, 
            and <strong>AI-powered insights</strong> to help you take control of your money.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white dark:bg-slate-700 rounded-lg p-3 text-center">
              <DollarSign className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
              <span className="text-xs text-gray-600 dark:text-gray-300">Transactions</span>
            </div>
            <div className="bg-white dark:bg-slate-700 rounded-lg p-3 text-center">
              <Wallet className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <span className="text-xs text-gray-600 dark:text-gray-300">Envelope Budget</span>
            </div>
            <div className="bg-white dark:bg-slate-700 rounded-lg p-3 text-center">
              <ListTodo className="w-6 h-6 text-purple-500 mx-auto mb-1" />
              <span className="text-xs text-gray-600 dark:text-gray-300">FinTasks</span>
            </div>
            <div className="bg-white dark:bg-slate-700 rounded-lg p-3 text-center">
              <BarChart3 className="w-6 h-6 text-amber-500 mx-auto mb-1" />
              <span className="text-xs text-gray-600 dark:text-gray-300">Insights</span>
            </div>
          </div>
        </div>

        {/* Getting Started */}
        <Section id="gettingStarted" title="🚀 Getting Started" icon={Sparkles} color="emerald">
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              To get the most out of FinList, follow these steps in order:
            </p>
            
            <Step number="1" title="Set Up Your Budget Envelopes First">
              Before adding any transactions, go to the <strong>Budget</strong> page and create your budget categories (envelopes). 
              Each envelope represents a spending category like "Groceries", "Transport", or "Entertainment". 
              Allocate money to each envelope based on your income.
            </Step>
            
            <Step number="2" title="Add Your Income">
              Tap the <strong>Transactions</strong> button (➕) on the main navigation bar. Select "Income" as the type, 
              enter the amount, and choose a category. Your income will be available to allocate to your budget envelopes.
            </Step>
            
            <Step number="3" title="Record Your Expenses">
              Use the same <strong>Transactions</strong> button to add expenses. Select "Expense", enter the amount, 
              and <strong>choose the appropriate budget category</strong>. The expense will be deducted from that envelope.
            </Step>
            
            <Step number="4" title="Create Shopping Lists">
              Plan your shopping by creating lists. Items on your shopping lists can be linked to budget categories, 
              so when you mark them as purchased, the spending is tracked.
            </Step>

            <Tip>
              Set up your budget envelopes before recording any transactions. This way, all your spending will be 
              properly tracked against your budget from day one!
            </Tip>
          </div>
        </Section>

        {/* Installation */}
        <Section id="installation" title="📲 Install FinList on Your Device" icon={Download} color="teal">
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              FinList is a <strong>Progressive Web App (PWA)</strong> - you can install it directly on your device for quick access, 
              offline support, and a native app-like experience. No app store required!
            </p>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-gray-600" />
                iPhone & iPad (iOS Safari)
              </h4>
              <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-2 ml-4 list-decimal">
                <li>Open FinList in <strong>Safari</strong> (not Chrome or other browsers)</li>
                <li>Tap the <strong>Share</strong> button (square with arrow pointing up) at the bottom of the screen</li>
                <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                <li>Give it a name (or keep "FinList") and tap <strong>Add</strong></li>
                <li>FinList will now appear as an app icon on your home screen!</li>
              </ol>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-green-600" />
                Android (Chrome)
              </h4>
              <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-2 ml-4 list-decimal">
                <li>Open FinList in <strong>Chrome</strong></li>
                <li>Look for the <strong>"Install"</strong> or <strong>"Add to Home Screen"</strong> banner at the bottom</li>
                <li>Or tap the <strong>three-dot menu</strong> (⋮) in the top-right corner</li>
                <li>Select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></li>
                <li>Tap <strong>Install</strong> to confirm</li>
              </ol>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                <Monitor className="w-5 h-5 text-blue-600" />
                Desktop (Chrome, Edge, or Brave)
              </h4>
              <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-2 ml-4 list-decimal">
                <li>Open FinList in Chrome, Edge, or Brave</li>
                <li>Look for the <strong>install icon</strong> (⊕) in the address bar on the right</li>
                <li>Click the icon and select <strong>"Install"</strong></li>
                <li>FinList will open in its own window and appear in your apps</li>
              </ol>
            </div>

            <Tip>
              Once installed, FinList works offline for viewing your data! You'll see your transactions, budgets, and 
              shopping lists even without internet. New changes will sync automatically when you're back online.
            </Tip>
          </div>
        </Section>

        {/* Transactions */}
        <Section id="transactions" title="💰 Transactions" icon={ArrowUpDown} color="blue">
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              The <strong>Transactions</strong> button is your central hub for recording all money movements - both income AND expenses.
            </p>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Adding Income
              </h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 ml-7">
                <li>• Tap the <strong>Transactions (➕)</strong> button in the navigation bar</li>
                <li>• Select <strong>"Income"</strong> as the transaction type</li>
                <li>• Enter the amount in your preferred currency</li>
                <li>• Choose a category (e.g., Salary, Freelance, Gifts)</li>
                <li>• Add a description and date</li>
                <li>• Tap "Save" to record the income</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-red-500" />
                Adding Expenses
              </h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 ml-7">
                <li>• Tap the <strong>Transactions (➕)</strong> button in the navigation bar</li>
                <li>• Select <strong>"Expense"</strong> as the transaction type</li>
                <li>• Enter the amount spent</li>
                <li>• <strong>Choose the budget category (envelope)</strong> - this is crucial!</li>
                <li>• The expense will be deducted from that category's budget</li>
                <li>• Add optional notes or attach a receipt</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-blue-500" />
                Transfers & Refunds
              </h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 ml-7">
                <li>• <strong>Transfers:</strong> Move money between your budget envelopes</li>
                <li>• <strong>Refunds:</strong> Record money returned to you (adds back to envelope)</li>
              </ul>
            </div>

            <Important>
              Every expense MUST be linked to a budget category (envelope). This is how FinList tracks your spending 
              against your budget. If you don't have a relevant category, create one in the Budget page first.
            </Important>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-500" />
                Multi-Currency Support
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                FinList supports transactions in multiple currencies with real-time exchange rates (updated hourly). 
                Record expenses in local currency while traveling - they'll be converted to your home currency automatically.
              </p>
            </div>
          </div>
        </Section>

        {/* Budget Envelopes */}
        <Section id="budgetEnvelopes" title="✉️ Envelope Budget System" icon={Wallet} color="purple">
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              FinList uses the <strong>envelope budgeting method</strong> - a proven system where you allocate money 
              into virtual "envelopes" for different spending categories. When an envelope is empty, you stop spending in that category!
            </p>

            <div className="bg-purple-50 dark:bg-slate-800 rounded-lg p-4 mb-4 border border-purple-100 dark:border-slate-700">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2">🎯 How Envelope Budgeting Works</h4>
              <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-2 ml-4 list-decimal">
                <li>Create envelopes for each spending category (e.g., Groceries, Rent, Entertainment)</li>
                <li>Allocate a specific amount of money to each envelope at the start of the month</li>
                <li>When you spend money, it's deducted from the appropriate envelope</li>
                <li>When an envelope is empty, you've reached your limit for that category</li>
                <li>Leftover money can roll over to next month or be reallocated</li>
              </ol>
            </div>

            <Step number="1" title="Access the Budget Page">
              Tap the <strong>Budget</strong> icon in the bottom navigation bar to view and manage your envelopes.
            </Step>

            <Step number="2" title="Create New Envelopes">
              Tap "Add Category" or the + button. Name your envelope (e.g., "Groceries"), set a monthly budget amount, 
              and optionally choose a color and icon.
            </Step>

            <Step number="3" title="Allocate Your Income">
              When you add income via Transactions, that money becomes available to allocate. 
              Distribute it across your envelopes based on your priorities.
            </Step>

            <Step number="4" title="Track Envelope Balances">
              Each envelope shows: budgeted amount, spent amount, and remaining balance. 
              Visual progress bars show how much you've used.
            </Step>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="bg-green-50 dark:bg-slate-900/60 border border-green-100 dark:border-slate-700 rounded-lg p-4">
                <h5 className="font-medium text-green-800 dark:text-white mb-2">✅ Envelope Healthy</h5>
                <p className="text-sm text-green-700 dark:text-gray-200">Plenty of budget remaining. You're on track!</p>
              </div>
              <div className="bg-yellow-50 dark:bg-slate-900/60 border border-yellow-100 dark:border-slate-700 rounded-lg p-4">
                <h5 className="font-medium text-yellow-800 dark:text-white mb-2">⚠️ Envelope Warning</h5>
                <p className="text-sm text-yellow-700 dark:text-gray-200">80% spent. Consider slowing down spending.</p>
              </div>
              <div className="bg-red-50 dark:bg-slate-900/60 border border-red-100 dark:border-slate-700 rounded-lg p-4">
                <h5 className="font-medium text-red-800 dark:text-white mb-2">🛑 Envelope Empty</h5>
                <p className="text-sm text-red-700 dark:text-gray-200">Budget exceeded! Transfer from another envelope or wait until next month.</p>
              </div>
              <div className="bg-blue-50 dark:bg-slate-900/60 border border-blue-100 dark:border-slate-700 rounded-lg p-4">
                <h5 className="font-medium text-blue-800 dark:text-white mb-2">💰 Envelope Rollover</h5>
                <p className="text-sm text-blue-700 dark:text-gray-200">Unused funds carry over to boost next month's budget.</p>
              </div>
            </div>

            <Tip>
              Start with broad categories (Food, Transport, Entertainment) and get more specific as you learn your spending patterns. 
              FinList's AI will help suggest optimal allocations based on your history!
            </Tip>
          </div>
        </Section>

        {/* Shopping Lists */}
        <Section id="shoppingLists" title="🛒 Shopping Lists" icon={ShoppingCart} color="teal">
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              Create and manage smart shopping lists that integrate with your budget. Plan ahead, shop efficiently, and never forget an item.
            </p>

            <Step number="1" title="Create a New List">
              From the Home page, tap "Create List" or the + button. Give your list a name (e.g., "Weekly Groceries") 
              and optionally link it to a budget category.
            </Step>

            <Step number="2" title="Add Items">
              Type to add items - FinList auto-completes from a catalogue of 1000+ common products. 
              Items are automatically categorized (Produce, Dairy, Bakery, etc.) for organized shopping.
            </Step>

            <Step number="3" title="Use Voice Input">
              Tap the <strong>microphone button</strong> to add items hands-free! Just speak the item name and it will be 
              automatically added with the correct category. Perfect for when your hands are busy.
            </Step>

            <Step number="4" title="Shop & Check Off">
              While shopping, tap items to mark them as purchased. The total updates in real-time, 
              and items move to the "Purchased" section at the bottom of your list.
            </Step>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2">✨ Smart Features</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <li>• <strong>Voice Input:</strong> Add items hands-free with speech recognition (tap the mic icon)</li>
                <li>• <strong>Auto-Complete:</strong> Type to see suggestions from 1000+ catalogue items</li>
                <li>• <strong>Smart Categories:</strong> Items are auto-categorized for organized shopping</li>
                <li>• <strong>Quick Add from Catalogue:</strong> Browse categories and tap to add common items</li>
                <li>• <strong>Quantity & Notes:</strong> Add quantities and notes to each item</li>
                <li>• <strong>Emoji Icons:</strong> Items show relevant emoji for quick visual recognition</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Share Your Lists
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                Share shopping lists with family and friends:
              </p>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-4">
                <li>• <strong>Share as Text:</strong> Send via WhatsApp, SMS, or any messaging app</li>
                <li>• <strong>Share as Link:</strong> Create a read-only link others can view in their browser</li>
                <li>• Links expire after 7 days for security</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-500" />
                Past Items & History
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Access your shopping history from the "Past Items" page. View previously purchased items, 
                restore deleted items from the trash, and quickly re-add frequent purchases to new lists.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2">📋 List Management</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                <li>• <strong>Archive Lists:</strong> Archive completed lists to keep your home page tidy</li>
                <li>• <strong>Duplicate Lists:</strong> Copy a list to create a template for recurring shops</li>
                <li>• <strong>Clear Purchased:</strong> Remove all checked-off items with one tap</li>
                <li>• <strong>Delete Items:</strong> Swipe or tap to remove items (recoverable from trash)</li>
              </ul>
            </div>

            <Tip>
              Use the <strong>catalogue browser</strong> at the bottom of the add item form to quickly add common items 
              by category - no typing required!
            </Tip>
          </div>
        </Section>

        {/* Tasks - FinTasks */}
        <Section id="tasks" title="📋 FinTasks" icon={ListTodo} color="indigo">
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              <strong>FinTasks</strong> is FinList's built-in task management system designed specifically for 
              financial to-dos and life admin. Stay on top of bills, subscriptions, and money-related tasks.
            </p>

            <div className="bg-indigo-50 dark:bg-slate-900/60 border border-indigo-100 dark:border-slate-700 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2">🎯 Access FinTasks</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Tap the <strong>Tasks</strong> icon in the bottom navigation bar to view and manage all your tasks.
              </p>
            </div>

            <Step number="1" title="Create a Task">
              Tap the + button to create a new task. Enter a title, set a due date, and optionally add notes or a reminder.
            </Step>

            <Step number="2" title="Set Reminders">
              Enable reminders to get notifications before tasks are due. Perfect for bill payments and subscription renewals.
            </Step>

            <Step number="3" title="Organize with Categories">
              Tag tasks with categories like "Bills", "Admin", "Shopping", or "Goals" to keep things organized.
            </Step>

            <Step number="4" title="Track Progress">
              Mark tasks as complete when done. View completed tasks in the history section.
            </Step>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2">💡 FinTasks Use Cases</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <li>• 📅 <strong>Bill Reminders:</strong> "Pay electricity bill by 15th"</li>
                <li>• 🔄 <strong>Subscription Checks:</strong> "Review Netflix subscription"</li>
                <li>• 📝 <strong>Financial Admin:</strong> "Submit tax documents"</li>
                <li>• 🎯 <strong>Savings Milestones:</strong> "Transfer £200 to savings"</li>
                <li>• 🛒 <strong>Shopping Prep:</strong> "Compare prices for new laptop"</li>
                <li>• 📞 <strong>Call Reminders:</strong> "Call bank about overdraft"</li>
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                Smart Notifications
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                FinTasks sends smart notifications to remind you about upcoming tasks. 
                You'll never miss a bill payment or important deadline again!
              </p>
            </div>

            <Tip>
              Use FinTasks to set recurring reminders for monthly bills. Create the task once with a repeat schedule, 
              and FinList will remind you every month!
            </Tip>
          </div>
        </Section>

        {/* Insights */}
        <Section id="insights" title="📊 Insights" icon={BarChart3} color="amber">
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              The <strong>Insights</strong> page is your financial dashboard - providing a clear picture of your 
              spending patterns, income trends, and budget health with beautiful charts and AI-powered analysis.
            </p>

            <div className="bg-amber-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2">📈 Access Insights</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Tap the <strong>Insights</strong> icon in the bottom navigation bar to view your financial analytics.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 dark:text-white mb-2">📊 Spending Breakdown</h5>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Visual pie charts and bar graphs showing where your money goes. 
                  See spending by category, week, or month.
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 dark:text-white mb-2">📈 Income vs Expenses</h5>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Track your cash flow over time. See if you're spending more than you earn 
                  and identify trends.
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 dark:text-white mb-2">✉️ Budget Health</h5>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  See which envelope budgets are on track, which are running low, 
                  and which have surplus funds.
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 dark:text-white mb-2">🤖 AI Analysis</h5>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Get personalized insights and recommendations from our AI 
                  based on your spending patterns.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2">🔍 What Insights Shows You</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <li>• <strong>Monthly Summary:</strong> Total income, expenses, and net savings</li>
                <li>• <strong>Category Breakdown:</strong> See top spending categories</li>
                <li>• <strong>Trends:</strong> Compare this month to previous months</li>
                <li>• <strong>Envelope Status:</strong> Quick view of all budget envelopes</li>
                <li>• <strong>Goal Progress:</strong> Track progress toward savings goals</li>
                <li>• <strong>AI Recommendations:</strong> Personalized tips to improve finances</li>
              </ul>
            </div>

            <Tip>
              Check Insights weekly to stay aware of your spending patterns. The AI will alert you 
              if you're overspending in any category before it becomes a problem!
            </Tip>
          </div>
        </Section>

        {/* Savings Goals */}
        <Section id="savingsGoals" title="🎯 Savings Goals" icon={Target} color="pink">
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              Set and track savings goals for things that matter to you - emergency funds, vacations, 
              new purchases, or any financial target you want to achieve.
            </p>

            <Step number="1" title="Create a Goal">
              Navigate to Savings Goals and tap "Create Goal". Give it a name (e.g., "Emergency Fund"), 
              set a target amount, and optionally add a target date.
            </Step>

            <Step number="2" title="Add Contributions">
              Regularly add contributions to your goals. You can set up automatic contributions 
              or manually add whenever you have extra money.
            </Step>

            <Step number="3" title="Track Progress">
              Watch your progress with visual progress bars and milestone celebrations. 
              FinList will notify you when you reach milestones!
            </Step>

            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-2">🎊 Goal Ideas</h4>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
                <div>• 🆘 Emergency Fund</div>
                <div>• ✈️ Vacation</div>
                <div>• 🏠 Home Deposit</div>
                <div>• 🎓 Education</div>
                <div>• 💻 New Laptop</div>
                <div>• 🚗 Car Fund</div>
                <div>• 💍 Wedding</div>
                <div>• 🎄 Christmas</div>
              </div>
            </div>

            <Tip>
              Set up automatic monthly contributions to your savings goals. Even small amounts add up over time. 
              FinList's AI will suggest optimal contribution amounts based on your income!
            </Tip>
          </div>
        </Section>

        {/* AI Features */}
        <Section id="aiFeatures" title="🤖 AI Features" icon={Sparkles} color="violet">
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              FinList is powered by AI throughout the app, helping you make smarter financial decisions.
            </p>

            <div className="grid grid-cols-1 gap-4">
              <div className="bg-violet-50 dark:bg-slate-900/60 border border-violet-100 dark:border-slate-700 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 dark:text-white mb-2">🛒 Smart Shopping Suggestions</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  AI suggests items based on your shopping history, predicts what you might need, 
                  and auto-completes item names as you type.
                </p>
              </div>
              <div className="bg-violet-50 dark:bg-slate-900/60 border border-violet-100 dark:border-slate-700 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 dark:text-white mb-2">💰 Budget Optimization</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  AI analyzes your spending patterns and suggests optimal budget allocations. 
                  Get recommendations for where to cut back and where you can afford more.
                </p>
              </div>
              <div className="bg-violet-50 dark:bg-slate-900/60 border border-violet-100 dark:border-slate-700 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 dark:text-white mb-2">📊 Spending Insights</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  AI identifies unusual spending, alerts you to potential overspending, 
                  and provides personalized financial advice.
                </p>
              </div>
              <div className="bg-violet-50 dark:bg-slate-900/60 border border-violet-100 dark:border-slate-700 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 dark:text-white mb-2">🎯 Goal Predictions</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  AI predicts when you'll reach your savings goals based on your contribution patterns 
                  and suggests ways to reach them faster.
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* Settings */}
        <Section id="settings" title="⚙️ Settings" icon={Settings} color="gray">
          <div className="space-y-4 mt-4">
            <p className="text-gray-600 dark:text-gray-300">
              Customize FinList to work exactly how you want.
            </p>

            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 dark:text-white mb-1">💱 Currency Settings</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Set your home currency and secondary currencies for multi-currency tracking.</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 dark:text-white mb-1">🔔 Notifications</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Configure reminder preferences, budget alerts, and task notifications.</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 dark:text-white mb-1">👤 Account</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Manage your profile, security settings, and data exports.</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 dark:text-white mb-1">🎨 Appearance</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Choose themes, colors, and display preferences.</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 dark:text-white mb-1">👨‍👩‍👧‍👦 Shared Lists</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Manage family members and shared list permissions.</p>
              </div>
            </div>
          </div>
        </Section>

        {/* FAQ */}
        <Section id="faq" title="❓ Frequently Asked Questions" icon={HelpCircle} color="rose">
          <div className="space-y-4 mt-4">
            
            <div className="border-b border-gray-100 dark:border-slate-700 pb-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">How do I add income to FinList?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Tap the <strong>Transactions (➕)</strong> button in the navigation bar, select "Income" as the type, 
                enter the amount and category, then save. Your income will be available to allocate to budget envelopes.
              </p>
            </div>

            <div className="border-b border-gray-100 dark:border-slate-700 pb-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">How do I record an expense?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Tap the <strong>Transactions (➕)</strong> button, select "Expense", enter the amount, 
                and choose the appropriate budget category (envelope). The expense will be deducted from that envelope's budget.
              </p>
            </div>

            <div className="border-b border-gray-100 dark:border-slate-700 pb-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">Why do I need to set up budget envelopes first?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Every expense must be linked to a budget category (envelope). This is how FinList tracks your spending 
                and helps you stay on budget. Create your envelopes before adding expenses so all spending is properly categorized.
              </p>
            </div>

            <div className="border-b border-gray-100 dark:border-slate-700 pb-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">How does the envelope system work?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Think of envelopes as virtual containers for your money. You allocate a set amount to each category 
                (like £200 for groceries). When you spend, it's deducted from that envelope. 
                When it's empty, you've reached your budget limit for that category.
              </p>
            </div>

            <div className="border-b border-gray-100 dark:border-slate-700 pb-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">What are FinTasks?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                FinTasks is FinList's built-in task manager designed for financial and life admin tasks. 
                Use it to track bill payments, subscription renewals, financial goals, and any money-related to-dos.
              </p>
            </div>

            <div className="border-b border-gray-100 dark:border-slate-700 pb-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">How do currency conversions work?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                FinList uses real-time exchange rates (updated hourly) via the Open Exchange Rates API. 
                Record transactions in any supported currency, and they'll be converted to your home currency automatically.
              </p>
            </div>

            <div className="border-b border-gray-100 dark:border-slate-700 pb-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">Can I share lists with family members?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Yes! Tap the <strong>Share</strong> button on any shopping list. You can share as text (for WhatsApp/SMS) 
                or create a shareable link that others can view in their browser. Links expire after 7 days.
              </p>
            </div>

            <div className="border-b border-gray-100 dark:border-slate-700 pb-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">How do I use voice input for shopping lists?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                When adding items to a shopping list, tap the <strong>microphone icon</strong> next to the input field. 
                Speak the item name clearly, and it will be automatically added with the correct category. 
                Works on most modern browsers (Chrome, Safari, Edge).
              </p>
            </div>

            <div className="border-b border-gray-100 dark:border-slate-700 pb-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">Can I recover deleted shopping items?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Yes! Deleted items are kept in a trash for 30 days. Go to <strong>Past Items</strong> in the menu 
                to view and restore recently deleted items.
              </p>
            </div>

            <div className="pb-4">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">How does AI help me manage money?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                FinList's AI provides smart suggestions, spending insights, budget optimization recommendations, 
                and goal predictions. It learns from your patterns to give increasingly personalized advice.
              </p>
            </div>

          </div>
        </Section>

        {/* Contact Support */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white mt-6">
          <div className="flex items-center gap-4">
            <Mail className="w-12 h-12 opacity-80" />
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-1">Need More Help?</h3>
              <p className="text-white/80 text-sm mb-3">
                Our support team is here to help you get the most out of FinList.
              </p>
              <div className="flex flex-wrap gap-2">
                {/* Using native <a> tag for mailto: to avoid React SPA event blocking */}
                <a
                  href={`mailto:${supportEmail}?subject=FinList Support Request`}
                  className="inline-flex items-center gap-2 bg-white dark:bg-emerald-600 text-emerald-600 dark:text-white px-4 py-2 rounded-lg font-medium hover:bg-emerald-50 dark:hover:bg-emerald-500 transition-colors cursor-pointer no-underline"
                >
                  <Mail className="w-4 h-4" />
                  Email Support
                </a>
                <button
                  type="button"
                  onClick={() => {
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(supportEmail);
                    } else {
                      // Fallback for older browsers
                      const textarea = document.createElement('textarea');
                      textarea.value = supportEmail;
                      document.body.appendChild(textarea);
                      textarea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textarea);
                    }
                  }}
                  className="inline-flex items-center gap-2 bg-emerald-100 dark:bg-emerald-800/70 text-emerald-700 dark:text-emerald-100 px-4 py-2 rounded-lg font-medium hover:bg-emerald-50 dark:hover:bg-emerald-700 transition-colors cursor-pointer"
                >
                  <Copy className="w-4 h-4" />
                  Copy Email
                </button>
              </div>
              <div className="mt-3 bg-white/10 rounded-lg px-3 py-2 inline-block">
                <p className="text-white text-sm font-medium">
                  📧 {supportEmail}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-400 text-sm mt-8 pb-8">
          <p>FinList v1.0 • Made with 💚 for smarter finances</p>
          <p className="mt-1">© 2025 FinList. All rights reserved.</p>
        </div>

      </div>
    </div>
  );
};

export default HelpPage;
